import React, { useState, useEffect, useRef } from 'react';
import api, { API_URL } from '../../api';

import '../../assets/css/user/user.css';
import { PrimaryButton, SecondaryButton } from '../../components/forms/buttons';
import { PlusSignIcon, FileExportIcon, ArrowDown01Icon, Edit02Icon, Delete01Icon, Search01Icon, CleanIcon, FilterVerticalIcon } from 'hugeicons-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import AddUserForm from '../../components/forms/AddUserForm';
import AddUserTabbedForm from '../../components/forms/AddUserTabbedForm';
import { useNavigate } from 'react-router-dom';
import DashboardContainer from '../../components/dashboard/DashboardContainer';
import { DataListInput, TextInput } from '../../components/forms/Input'
import EditUser from '../../components/user/EditUser';
import DeleteUser from '../../components/user/DeleteUser';
import Cookies from 'js-cookie';
import 'react-toastify/dist/ReactToastify.css';
import UserImportEmbedded from '../../components/user/UserImportEmbedded';
import UserRolesPage from '../dashboard/UserRolesPage'; // Add this import
import { hasRole } from '../../services/userPosition';
import TableLoading from '../../components/loading/TableLoading';
import EmptyState from '../../components/common/EmptyState';

const UserPageContent = () => {
    const [users, setUsers] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [previousPage, setPreviousPage] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [showTabbedForm, setShowTabbedForm] = useState(false); // Keep this for the tabbed form
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);
    const [isSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    // Filter popup state
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterHospital, setFilterHospital] = useState('');
    const [filterPosition, setFilterPosition] = useState('');
    const [hospitalOptions, setHospitalOptions] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [selectedUserData, setSelectedUserData] = useState(null);
    const [activeTab, setActiveTab] = useState('users');
    const [selectedUserId, setSelectedUserId] = useState(null);

    const navigate = useNavigate();
    const handleUserAdded = (newUser) => {
        setUsers((prevUsers) => [...prevUsers, newUser]);
    };

    const handleUsersImported = () => {
        fetchUsers();
    };

    const handleUserUpdated = (updatedUser) => {
        setUsers((prevUsers) =>
            prevUsers.map((user) => (user.id === updatedUser.id ? updatedUser : user))
        );
    };

    const handleUserDeleted = (userId) => {
        setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
    };


    // Fetch users with filters
    const fetchUsers = async (filters = {}, url = null) => {
        setIsOverlayVisible(true);
        try {
            const params = {};
            if (filters.hospital) params.hospital = filters.hospital;
            if (filters.position) params.position = filters.position;
            let response;
            if (url) {
                response = await api.get(url);
            } else {
                response = await api.get(`${API_URL}/user/list_users/`, { params });
            }
            if (response.status === 200) {
                let usersData = response.data;
                if (usersData && Array.isArray(usersData.results)) {
                    setUsers(usersData.results);
                    setNextPage(usersData.next);
                    setPreviousPage(usersData.previous);
                } else if (Array.isArray(usersData)) {
                    setUsers(usersData);
                    setNextPage(null);
                    setPreviousPage(null);
                } else {
                    setUsers([]);
                    setNextPage(null);
                    setPreviousPage(null);
                }
            } else {
                setUsers([]);
                setNextPage(null);
                setPreviousPage(null);
            }
        } catch (error) {
            setUsers([]);
            setNextPage(null);
            setPreviousPage(null);
        } finally {
            setIsOverlayVisible(false);
        }
    };

    // Fetch hospital and position options for filter popup
    const fetchFilterOptions = async () => {
        try {
            const token = Cookies.get('accessToken');
            // Hospitals
            const hospitalsRes = await api.get(`${API_URL}/hospitals/`, { headers: { 'Authorization': `Bearer ${token}` } });
            let hospitalsData = [];
            if (hospitalsRes.data && hospitalsRes.data.results && Array.isArray(hospitalsRes.data.results)) {
                hospitalsData = hospitalsRes.data.results;
            } else if (Array.isArray(hospitalsRes.data)) {
                hospitalsData = hospitalsRes.data;
            }
            setHospitalOptions(hospitalsData.map(h => h.name));
            // Positions
            const positionsRes = await api.get(`${API_URL}/position/`, { headers: { 'Authorization': `Bearer ${token}` } });
            setPositionOptions(positionsRes.data.map(p => p.name));
        } catch (error) {
            setHospitalOptions([]);
            setPositionOptions([]);
        }
    };

    // Lazy loading - only load data when user is on users tab
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [filtersLoaded, setFiltersLoaded] = useState(false);

    useEffect(() => {
        // Only load users data if user is on users tab and data hasn't been loaded yet
        if (activeTab === 'users' && !usersLoaded) {
            const loadUsersData = async () => {
                setIsOverlayVisible(true);

                // Load critical data first
                await fetchUsers();

                // Load filter options with a delay to prevent API overload
                setTimeout(() => {
                    fetchFilterOptions();
                }, 200);

                setUsersLoaded(true);
                setIsOverlayVisible(false);
            };

            loadUsersData();
        }
    }, [activeTab, usersLoaded]);

    // Reset data when user switches tabs
    useEffect(() => {
        if (activeTab !== 'users') {
            setUsersLoaded(false);
        }
    }, [activeTab]);
    const handleChangePage = (url, direction) => {
        fetchUsers({}, url);
        setCurrentPage((prev) => direction === 'next' ? prev + 1 : prev - 1);
    };

    const handleAddNewUser = () => {
        setShowTabbedForm(true);
    };

    const closePopup = () => {
        setIsPopupOpen(false);
        setIsEditPopupOpen(false);
        setIsDeletePopupOpen(false);
        setShowTabbedForm(false);
        fetchUsers();
    };


    const handleRowClicks = (user_id) => {
        navigate(`/users/${user_id}`);
    };


    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setIsDeletePopupOpen(true);
    };

    const handleEditUser = (userId) => {
        const user = users.find((user) => user.user_id === userId);
        if (user) {
            // Format the user data with all necessary fields
            const formattedUserData = {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
                hospital: user.hospital,
                position: user.position,
                hospital_id: user.hospital_id,
                position_id: user.position_id,
                account_id: user.account_id
            };
            setSelectedUserId(userId);
            setSelectedUserData(formattedUserData);
            setIsEditPopupOpen(true);
        }
    };


    // Filter users by search only (popup filters handled by backend)
    const filteredUsers = users.filter(user => {
        const lowerCaseUsername = user?.username?.toLowerCase() || '';
        const lowerCaseFirstName = user?.first_name?.toLowerCase() || '';
        const lowerCaseLastName = user?.last_name?.toLowerCase() || '';
        const lowerCaseEmail = user?.email?.toLowerCase() || '';
        const lowerCaseDateCreated = user?.date_added?.toLowerCase() || '';
        const lowerCaseQuery = searchQuery.toLowerCase();
        return (
            lowerCaseUsername.includes(lowerCaseQuery) ||
            lowerCaseFirstName.includes(lowerCaseQuery) ||
            lowerCaseLastName.includes(lowerCaseQuery) ||
            lowerCaseEmail.includes(lowerCaseQuery) ||
            lowerCaseDateCreated.includes(lowerCaseQuery)
        );
    });


    const handleExport = () => {
        const excelData = filteredUsers.map(user => ({
            "First Name": user.first_name,
            "Last Name": user.last_name,
            "Username": user.username,
            "Email": user.email,
            "Hospital/Facility": user.hospital || 'Not Assigned',
            "User Role": user.position,
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        saveAs(blob, 'users.xlsx');
    };



    // Filter popup logic
    const filterPopupRef = useRef(null);
    useEffect(() => {
        if (!isFilterOpen) return;
        const handleClickOutside = (e) => {
            if (filterPopupRef.current && !filterPopupRef.current.contains(e.target)) {
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isFilterOpen]);

    const handleOpenFilter = () => {
        setIsFilterOpen(true);
    };
    const handleResetFilters = () => {
        setFilterHospital('');
        setFilterPosition('');
        setIsFilterOpen(false);
        fetchUsers();
    };
    const handleApplyFilters = () => {
        const filters = {
            hospital: filterHospital,
            position: filterPosition,
        };
        fetchUsers(filters);
    };

    const handleHospitalChange = (e) => {
        setFilterHospital(e.target.value);
    };

    const handlePositionChange = (e) => {
        setFilterPosition(e.target.value);
    };

    return (
        <div className="user-content">
            {/* Add User Tabbed Form */}
            {showTabbedForm && (
                <div className="overlay">
                    <AddUserTabbedForm
                        isOpen={showTabbedForm}
                        onClose={() => setShowTabbedForm(false)}
                        onUserAdded={handleUserAdded}
                        onUsersImported={handleUsersImported}
                    />
                </div>
            )}

            {/* Filter Popup */}
            {isFilterOpen && (
                <div className="filter-popup popup-overlay">
                    <div className="filter-section add-target-popup" ref={filterPopupRef}>
                        <h3>Filter Users</h3>
                        <div className="filter-item">
                            <label>Hospital/Facility</label>
                            <select value={filterHospital} onChange={handleHospitalChange}>
                                <option value="">All</option>
                                {hospitalOptions.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                        </div>
                        <div className="filter-item">
                            <label>Position</label>
                            <select value={filterPosition} onChange={handlePositionChange}>
                                <option value="">All</option>
                                {positionOptions.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <button className="reset-button" onClick={handleResetFilters}>Reset Filter</button>
                        <button className="apply-button" onClick={() => {
                            setIsFilterOpen(false);
                            handleApplyFilters();
                        }}>Apply</button>
                    </div>
                </div>
            )}

            <div className="buttons-search">
                <div className="search-users">
                    <TextInput
                        iconClass={<Search01Icon />}
                        type={'search'}
                        placeholder={'Search by username'}
                        value={searchQuery}
                        setValue={setSearchQuery}
                    />
                </div>
                <div className="add-user-bttns">
                    <div className="export-bttn" onClick={handleExport}>
                        <FileExportIcon size={24} />
                        <p>Export Users</p>
                    </div>
                    {!hasRole(["Manager", "User"]) && (
                        <div className="primary-btn">
                            <PrimaryButton
                                isLoading={isSubmitting}
                                onClick={handleAddNewUser}
                                processingText={'Submitting'}
                                iconClass={<PlusSignIcon />}
                                buttonText={'Add New User'}
                            />
                        </div>
                    )}
                    <div>
                        <SecondaryButton
                            onClick={handleOpenFilter}
                            buttonText="Filter"
                            iconClass={<FilterVerticalIcon />}
                            className="filter-button"
                        />
                    </div>
                </div>
                {(filterHospital || filterPosition) && (
                    <div className="filter-summary">
                        <div className="filter-tags">
                            {filterHospital && (
                                <span className="filter-tag">
                                    <h3>Hospital:</h3>
                                    <span>{filterHospital}</span>
                                </span>
                            )}
                            {filterPosition && (
                                <span className="filter-tag">
                                    <h3>Position:</h3>
                                    <span>{filterPosition}</span>
                                </span>
                            )}
                        </div>
                        <p className="clear-all-filters"
                            onClick={() => {
                                setFilterHospital('');
                                setFilterPosition('');
                                fetchUsers();
                            }}>Clear All</p>
                    </div>
                )}
            </div>

            <div className="tabs">
                <div
                    className={`tab ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    All Users
                </div>

            </div>

            {activeTab === 'users' && (
                <div className="table-container">
                    {isOverlayVisible && <TableLoading />}
                    {filteredUsers.length > 0 ? (
                        <>
                            <table>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Email</th>
                                        <th>Names</th>
                                        <th>Hospital/Facility</th>
                                        <th>Position</th>
                                        {hasRole(["Admin", "User Editor", "Super User"]) ? <th>Action</th> : <></>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {isEditPopupOpen && (
                                        <EditUser
                                            userId={selectedUserId}
                                            userData={selectedUserData}
                                            onClose={() => {
                                                setIsEditPopupOpen(false);
                                                fetchUsers();
                                            }}
                                            onSubmit={() => {
                                                setIsEditPopupOpen(false);
                                                fetchUsers();
                                            }}
                                        />
                                    )}
                                    {isDeletePopupOpen && (
                                        <DeleteUser
                                            isOpen={isDeletePopupOpen}
                                            userId={selectedUser}
                                            onClose={() => {
                                                setIsDeletePopupOpen(false);
                                                fetchUsers();
                                            }}
                                            deleteUser={() => {
                                                setIsDeletePopupOpen(false);
                                                fetchUsers();
                                            }}
                                            account_id={users.find(u => u.user_id === selectedUser)?.account_id}
                                            userName={`${users.find(u => u.user_id === selectedUser)?.first_name} ${users.find(u => u.user_id === selectedUser)?.last_name}`}
                                        />
                                    )}
                                    {filteredUsers.map((user) => (
                                        <React.Fragment key={user.user_id}>
                                            <tr
                                                key={user.user_id}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleRowClicks(user.user_id)}
                                            >
                                                <td>{user.account_id}</td>
                                                <td className='td-img'>
                                                    <img src='/logo.png' alt="" />
                                                    {user.username || 'N/A'}
                                                </td>
                                                <td>{user.first_name} {user.last_name}</td>
                                                <td>{user.hospital || 'Not Assigned'}</td>
                                                <td>{user.position}</td>
                                                {hasRole(["Admin", "User Editor", "Super User"]) ?
                                                    <td>
                                                        <div className="action-buttons">
                                                            <Edit02Icon
                                                                size={32}
                                                                className="edit-icon"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditUser(user.user_id);
                                                                }}
                                                            />
                                                            <Delete01Icon
                                                                size={32}
                                                                className="delete-icon"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteClick(user.user_id);
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                    :
                                                    <></>

                                                }

                                            </tr>

                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                            <div className="pagination-controls">
                                <button className={`previous ${!previousPage ? 'disabled' : ''}`} onClick={() => handleChangePage(previousPage, 'prev')} disabled={!previousPage}>Previous</button>
                                <span>Page {currentPage}</span>
                                <button className={`next ${!nextPage ? 'disabled' : ''}`} onClick={() => handleChangePage(nextPage, 'next')} disabled={!nextPage}>Next</button>
                            </div>
                        </>
                    ) : (
                        <EmptyState
                            icon="user"
                            title="No users found"
                            message="There are no users to display."
                            suggestion="Try adjusting your search or filters, or add a new user."
                        />
                    )}
                </div>
            )}

            {activeTab === 'userRoles' && (
                <div>

                    <UserRolesPage />
                </div>
            )}
        </div>
    );
};

const UserPage = () => {
    return (
        <DashboardContainer content={<UserPageContent />} pageTitle={'Users'} />
    );
};

export default UserPage;